if (!customElements.get('social-gallery')) {
  class SocialGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';
      this.cards = Array.from(this.querySelectorAll('[data-social-gallery-card]'));
      this.templates = Array.from(this.querySelectorAll('[data-social-gallery-template]'));
      this.search = this.querySelector('[data-social-gallery-search]');
      this.filterButtons = Array.from(this.querySelectorAll('[data-social-gallery-filter]'));
      this.loadMoreButton = this.querySelector('[data-social-gallery-load-more]');
      this.loadMoreSentinel = this.querySelector('[data-social-gallery-load-sentinel]');
      this.emptyMessage = this.querySelector('[data-social-gallery-empty]');
      this.count = this.querySelector('[data-social-gallery-count]');
      this.dialog = this.querySelector('[data-social-gallery-dialog]');
      this.modalContent = this.querySelector('[data-social-gallery-modal-content]');
      this.trackElement = this.querySelector('[data-social-gallery-track]');
      this.carouselPrevious = this.querySelector('[data-social-gallery-carousel-previous]');
      this.carouselNext = this.querySelector('[data-social-gallery-carousel-next]');
      this.initialCount = Number.parseInt(this.dataset.initialCount, 10) || 10;
      this.loadCount = Number.parseInt(this.dataset.loadCount, 10) || 10;
      this.loadMode = this.dataset.loadMode || 'button';
      this.isCarousel = this.dataset.layout === 'carousel';
      this.deepLinksEnabled = this.dataset.deepLinks === 'true';
      this.dialogType = this.dataset.dialogType || 'feed';
      this.trackingEnabled = this.dataset.trackingEnabled === 'true';
      this.visibleLimit = this.isCarousel ? this.cards.length : this.initialCount;
      this.activeFilter = '';
      this.activeIndex = -1;
      this.lastTrigger = null;
      this.isClosingFromHistory = false;
      this.impressedPostIds = new Set();

      this.handleClickBound = this.handleClick.bind(this);
      this.handleSearchBound = this.handleSearch.bind(this);
      this.handleDialogBackdropBound = this.handleDialogBackdrop.bind(this);
      this.handleDialogCloseBound = this.handleDialogClose.bind(this);
      this.handlePopStateBound = this.handlePopState.bind(this);
      this.handleTrackKeydownBound = this.handleTrackKeydown.bind(this);
      this.handleDialogKeydownBound = this.handleDialogKeydown.bind(this);
      this.updateCarouselControlsBound = this.updateCarouselControls.bind(this);
      this.updateMediaCarouselBound = this.scheduleMediaCarouselUpdate.bind(this);

      this.addEventListener('click', this.handleClickBound);
      this.search?.addEventListener('input', this.handleSearchBound);
      this.dialog?.addEventListener('click', this.handleDialogBackdropBound);
      this.dialog?.addEventListener('close', this.handleDialogCloseBound);
      this.dialog?.addEventListener('keydown', this.handleDialogKeydownBound);
      this.trackElement?.addEventListener('scroll', this.updateCarouselControlsBound, {
        passive: true,
      });
      this.trackElement?.addEventListener('keydown', this.handleTrackKeydownBound);
      window.addEventListener('resize', this.updateCarouselControlsBound);
      window.addEventListener('popstate', this.handlePopStateBound);

      this.observeLoadMore();
      this.render();
      this.observeCards();
      this.track('gallery_view', { post_count: this.cards.length });
      requestAnimationFrame(() => this.openFromUrl());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClickBound);
      this.search?.removeEventListener('input', this.handleSearchBound);
      this.dialog?.removeEventListener('click', this.handleDialogBackdropBound);
      this.dialog?.removeEventListener('close', this.handleDialogCloseBound);
      this.dialog?.removeEventListener('keydown', this.handleDialogKeydownBound);
      this.trackElement?.removeEventListener('scroll', this.updateCarouselControlsBound);
      this.trackElement?.removeEventListener('keydown', this.handleTrackKeydownBound);
      window.removeEventListener('resize', this.updateCarouselControlsBound);
      window.removeEventListener('popstate', this.handlePopStateBound);
      this.cardObserver?.disconnect();
      this.loadMoreObserver?.disconnect();
      this.teardownMediaCarousel();
      this.dataset.initialized = 'false';
    }

    get query() {
      return this.search?.value.trim().toLowerCase() || '';
    }

    get matchingCards() {
      return this.cards.filter((card) => {
        const matchesQuery = !this.query || card.dataset.filterText.includes(this.query);
        const matchesFilter =
          !this.activeFilter || (card.dataset.filterTags || '').includes(this.activeFilter);
        return matchesQuery && matchesFilter;
      });
    }

    handleClick(event) {
      const productLink = event.target.closest('[data-social-gallery-product-link]');
      if (productLink && this.contains(productLink)) {
        this.track('product_click', {
          post_id: productLink.dataset.postId,
          external_content_id: productLink.dataset.externalContentId,
          content_source: productLink.dataset.contentSource,
          linked_product_id: productLink.dataset.productId,
        });
        return;
      }

      const filterButton = event.target.closest('[data-social-gallery-filter]');
      if (filterButton && this.contains(filterButton)) {
        this.activeFilter = filterButton.dataset.socialGalleryFilter || '';
        this.filterButtons.forEach((button) => {
          const isActive = button === filterButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', String(isActive));
        });
        this.visibleLimit = this.isCarousel ? this.cards.length : this.initialCount;
        this.render();
        this.track('filter', { filter: this.activeFilter || 'all' });
        return;
      }

      const openButton = event.target.closest('[data-social-gallery-open]');
      if (openButton && this.contains(openButton)) {
        const card = openButton.closest('[data-social-gallery-card]');
        this.openPost(Number.parseInt(card.dataset.galleryIndex, 10), openButton, 'push');
        return;
      }

      if (event.target.closest('[data-social-gallery-load-more]')) {
        this.visibleLimit += this.loadCount;
        this.render();
        return;
      }

      if (event.target.closest('[data-social-gallery-carousel-previous]')) {
        this.scrollCarousel(-1);
        return;
      }

      if (event.target.closest('[data-social-gallery-carousel-next]')) {
        this.scrollCarousel(1);
        return;
      }

      const mediaPage = event.target.closest('[data-social-gallery-media-page]');
      if (mediaPage && this.contains(mediaPage)) {
        this.goToMediaCarouselSlide(
          Number.parseInt(mediaPage.dataset.socialGalleryMediaPage, 10),
        );
        return;
      }

      if (event.target.closest('[data-social-gallery-close]')) {
        this.dialog?.close();
        return;
      }

      if (event.target.closest('[data-social-gallery-previous]')) {
        this.move(-1);
        return;
      }

      if (event.target.closest('[data-social-gallery-next]')) this.move(1);
    }

    handleSearch() {
      this.visibleLimit = this.query ? this.cards.length : this.initialCount;
      this.render();
    }

    handleTrackKeydown(event) {
      if (!this.isCarousel || event.target !== this.trackElement) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.scrollCarousel(event.key === 'ArrowLeft' ? -1 : 1);
    }

    handleDialogKeydown(event) {
      const mediaCarousel = event.target.closest?.('[data-social-gallery-media-carousel]');
      if (!mediaCarousel || mediaCarousel !== this.mediaCarousel) return;
      if (!window.matchMedia?.('(max-width: 749px)').matches) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.goToMediaCarouselSlide(
        this.mediaCarouselIndex + (event.key === 'ArrowLeft' ? -1 : 1),
      );
    }

    handleDialogBackdrop(event) {
      if (event.target === this.dialog) this.dialog.close();
    }

    handleDialogClose() {
      const activePostId = this.getActiveCard()?.dataset.postId;
      this.pauseVideos();
      this.teardownMediaCarousel();
      this.modalContent?.replaceChildren();

      if (!this.isClosingFromHistory) this.clearDeepLink();
      this.isClosingFromHistory = false;

      this.lastTrigger?.focus();
      this.activeIndex = -1;
      this.track('post_close', { post_id: activePostId });
    }

    handlePopState() {
      const opened = this.openFromUrl();

      if (!opened && this.dialog?.open) {
        this.isClosingFromHistory = true;
        this.dialog.close();
      }
    }

    render() {
      const matches = this.matchingCards;
      const visibleCards = matches.slice(0, this.visibleLimit);
      const visibleSet = new Set(visibleCards);

      this.cards.forEach((card) => {
        card.hidden = !visibleSet.has(card);
      });

      if (this.count) this.count.textContent = String(matches.length);
      if (this.emptyMessage) this.emptyMessage.hidden = matches.length !== 0;
      const hasMore = matches.length > this.visibleLimit;
      const supportsInfinite = this.loadMode === 'infinite' && Boolean(this.loadMoreObserver);
      if (this.loadMoreButton) this.loadMoreButton.hidden = !hasMore || supportsInfinite;
      if (this.loadMoreSentinel) {
        this.loadMoreSentinel.hidden = !hasMore || !supportsInfinite;
      }
      this.updateCarouselControls();
    }

    observeLoadMore() {
      if (
        this.isCarousel ||
        this.loadMode !== 'infinite' ||
        !this.loadMoreSentinel ||
        !('IntersectionObserver' in window)
      ) {
        return;
      }

      this.loadMoreObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          if (this.matchingCards.length <= this.visibleLimit) return;

          this.visibleLimit += this.loadCount;
          this.render();
        },
        { rootMargin: '400px 0px' },
      );
      this.loadMoreObserver.observe(this.loadMoreSentinel);
    }

    openPost(index, trigger = null, historyMode = null) {
      const template = this.templates.find(
        (item) => Number.parseInt(item.dataset.galleryIndex, 10) === index,
      );
      if (!template || !this.dialog || !this.modalContent) return false;

      this.pauseVideos();
      this.teardownMediaCarousel();
      this.modalContent.replaceChildren(template.content.cloneNode(true));
      this.activeIndex = index;
      this.lastTrigger = trigger || this.lastTrigger;

      const card = this.cards.find(
        (item) => Number.parseInt(item.dataset.galleryIndex, 10) === index,
      );
      const dialogLabel = trigger?.getAttribute('aria-label') || 'SNS投稿の詳細';
      this.dialog.setAttribute('aria-label', dialogLabel);

      if (!this.dialog.open) this.dialog.showModal();
      const modalShell = this.dialog.querySelector('.social-gallery-modal__shell');
      if (modalShell) modalShell.scrollTop = 0;
      this.setupMediaCarousel();
      this.dialog.querySelector('[data-social-gallery-close]')?.focus();

      const video = this.modalContent.querySelector('video:not(.social-gallery-modal__carousel-video)');
      video?.addEventListener(
        'play',
        () => this.track('video_play', { post_id: card?.dataset.postId }),
        { once: true },
      );
      if (video) {
        video.muted = true;
        const playback = video.play();
        playback?.catch?.(() => {});
      }

      this.track('post_open', {
        post_id: card?.dataset.postId,
        external_content_id: card?.dataset.externalContentId,
        content_source: card?.dataset.contentSource,
        tracking_label: card?.dataset.trackingLabel,
        media_type: card?.dataset.mediaType,
      });

      if (historyMode && card) this.setDeepLink(card.dataset.postId, historyMode);
      return true;
    }

    move(direction) {
      const visibleCards = this.cards.filter((card) => !card.hidden);
      if (!visibleCards.length) return;

      const currentPosition = visibleCards.findIndex(
        (card) => Number.parseInt(card.dataset.galleryIndex, 10) === this.activeIndex,
      );
      const nextPosition = (currentPosition + direction + visibleCards.length) % visibleCards.length;
      const nextCard = visibleCards[nextPosition];
      const nextIndex = Number.parseInt(nextCard.dataset.galleryIndex, 10);
      this.openPost(nextIndex, nextCard.querySelector('[data-social-gallery-open]'), 'replace');
    }

    scrollCarousel(direction) {
      if (!this.trackElement) return;

      const visibleCard = this.cards.find((card) => !card.hidden);
      const gap = Number.parseFloat(getComputedStyle(this.trackElement).columnGap) || 0;
      const distance =
        (visibleCard?.getBoundingClientRect().width || this.trackElement.clientWidth) + gap;
      this.trackElement.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    updateCarouselControls() {
      if (!this.isCarousel || !this.trackElement) return;

      const tolerance = 2;
      if (this.carouselPrevious) {
        this.carouselPrevious.disabled = this.trackElement.scrollLeft <= tolerance;
      }
      if (this.carouselNext) {
        const remaining =
          this.trackElement.scrollWidth -
          this.trackElement.clientWidth -
          this.trackElement.scrollLeft;
        this.carouselNext.disabled = remaining <= tolerance;
      }
    }

    setupMediaCarousel() {
      this.mediaCarousel = this.modalContent?.querySelector(
        '[data-social-gallery-media-carousel]',
      );
      if (!this.mediaCarousel) return;

      this.mediaCarouselItems = Array.from(
        this.mediaCarousel.querySelectorAll('[data-social-gallery-carousel-item]'),
      );
      this.mediaCarouselPages = Array.from(
        this.modalContent.querySelectorAll('[data-social-gallery-media-page]'),
      );
      this.mediaCarouselCounter = this.modalContent.querySelector(
        '[data-social-gallery-media-counter]',
      );
      this.mediaCarouselIndex = -1;
      this.mediaCarousel.scrollLeft = 0;
      this.mediaCarousel.tabIndex =
        this.mediaCarouselItems.length > 1 && window.matchMedia?.('(max-width: 749px)').matches
          ? 0
          : -1;
      this.mediaCarousel.addEventListener('scroll', this.updateMediaCarouselBound, {
        passive: true,
      });
      this.updateMediaCarousel();
    }

    teardownMediaCarousel() {
      this.mediaCarousel?.removeEventListener('scroll', this.updateMediaCarouselBound);
      if (this.mediaCarouselFrame) window.cancelAnimationFrame?.(this.mediaCarouselFrame);
      this.mediaCarouselFrame = null;
      this.mediaCarousel = null;
      this.mediaCarouselItems = [];
      this.mediaCarouselPages = [];
      this.mediaCarouselCounter = null;
      this.mediaCarouselIndex = 0;
    }

    goToMediaCarouselSlide(index) {
      if (!this.mediaCarousel || !this.mediaCarouselItems?.length) return;

      const targetIndex = Math.max(
        0,
        Math.min(this.mediaCarouselItems.length - 1, index),
      );
      const targetItem = this.mediaCarouselItems[targetIndex];
      this.setMediaCarouselState(targetIndex);
      this.mediaCarousel.scrollTo({
        left: targetItem.offsetLeft,
        behavior: 'smooth',
      });
    }

    scheduleMediaCarouselUpdate() {
      if (this.mediaCarouselFrame) return;

      if (!window.requestAnimationFrame) {
        this.updateMediaCarousel();
        return;
      }

      this.mediaCarouselFrame = window.requestAnimationFrame(() => {
        this.mediaCarouselFrame = null;
        this.updateMediaCarousel();
      });
    }

    updateMediaCarousel() {
      if (!this.mediaCarousel || !this.mediaCarouselItems?.length) return;

      const carouselLeft = this.mediaCarousel.getBoundingClientRect().left;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      this.mediaCarouselItems.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - carouselLeft);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });
      this.setMediaCarouselState(nearestIndex);
    }

    setMediaCarouselState(index) {
      if (this.mediaCarouselIndex === index) return;

      this.mediaCarouselIndex = index;
      if (this.mediaCarouselCounter) {
        this.mediaCarouselCounter.textContent = `${index + 1} / ${this.mediaCarouselItems.length}`;
      }
      this.mediaCarouselPages?.forEach((page, pageIndex) => {
        const isActive = pageIndex === index;
        page.classList.toggle('is-active', isActive);
        if (isActive) page.setAttribute('aria-current', 'page');
        else page.removeAttribute('aria-current');
      });

      this.mediaCarouselItems.forEach((item, itemIndex) => {
        if (itemIndex === index) return;
        item.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    openFromUrl() {
      if (!this.deepLinksEnabled) return false;

      const url = new URL(window.location.href);
      const collectionId = url.searchParams.get('collection_id');
      const postId = url.searchParams.get('contents_id');
      const dialogType = url.searchParams.get('dialog_type');
      if (collectionId !== this.dataset.galleryId || !postId || dialogType !== this.dialogType) return false;

      const card = this.cards.find((item) => item.dataset.postId === postId);
      if (!card) return false;

      return this.openPost(
        Number.parseInt(card.dataset.galleryIndex, 10),
        card.querySelector('[data-social-gallery-open]'),
      );
    }

    setDeepLink(postId, historyMode) {
      if (!this.deepLinksEnabled || !postId) return;

      const url = new URL(window.location.href);
      url.searchParams.set('collection_id', this.dataset.galleryId);
      url.searchParams.set('contents_id', postId);
      url.searchParams.set('dialog_type', this.dialogType);

      const method = historyMode === 'push' ? 'pushState' : 'replaceState';
      window.history[method]({}, '', url);
    }

    clearDeepLink() {
      if (!this.deepLinksEnabled) return;

      const url = new URL(window.location.href);
      if (url.searchParams.get('collection_id') !== this.dataset.galleryId) return;

      url.searchParams.delete('collection_id');
      url.searchParams.delete('contents_id');
      url.searchParams.delete('dialog_type');
      window.history.replaceState({}, '', url);
    }

    pauseVideos() {
      this.modalContent?.querySelectorAll('video').forEach((video) => video.pause());
    }

    getActiveCard() {
      return this.cards.find(
        (card) => Number.parseInt(card.dataset.galleryIndex, 10) === this.activeIndex,
      );
    }

    observeCards() {
      if (!('IntersectionObserver' in window)) return;

      this.cardObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;

            const card = entry.target;
            const postId = card.dataset.postId;
            if (!postId || this.impressedPostIds.has(postId) || card.hidden) return;

            this.impressedPostIds.add(postId);
            this.track('post_impression', {
              post_id: postId,
              external_content_id: card.dataset.externalContentId,
              content_source: card.dataset.contentSource,
              tracking_label: card.dataset.trackingLabel,
              media_type: card.dataset.mediaType,
            });
          });
        },
        { threshold: 0.5 },
      );

      this.cards.forEach((card) => this.cardObserver.observe(card));
    }

    track(name, detail = {}) {
      const payload = {
        gallery_id: this.dataset.galleryId,
        source_mode: this.dataset.sourceMode,
        page_type: this.dataset.pageType,
        product_id: this.dataset.productId || undefined,
        ...detail,
      };

      this.dispatchEvent(
        new CustomEvent(`social-gallery:${name}`, {
          bubbles: true,
          detail: payload,
        }),
      );

      if (!this.trackingEnabled) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `social_gallery_${name}`, ...payload });
    }
  }

  customElements.define('social-gallery', SocialGallery);
}
